import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

const kFallbackMessage = 'That SharePoint site could not be opened. Check the link and try again.'
const kReconnectMessage = 'Your SharePoint connection needs to be reconnected. Reconnect it in Settings and try again.'
const kRateLimitMessage = 'SharePoint is rate limiting requests right now. Try again shortly.'
const kSearchFallbackMessage = 'Could not search SharePoint sites. Try again or enter a site URL.'

const kMessageByStatus: Record<number, string> = {
  401: kReconnectMessage,
  403: 'You do not have access to that SharePoint site, or your SharePoint connection needs to be reconnected.',
  404: 'That SharePoint site was not found. Check the link and try again.',
  // The backend returns 409 when the connected account itself is REAUTH_REQUIRED.
  409: kReconnectMessage,
  429: kRateLimitMessage,
}

/**
 * Turn a resolve-site failure into something safe to show a user.
 *
 * The raw `Error.message` is not presentable: a payload/schema failure carries
 * contract diagnostics, and a server `detail` can carry a serialized FastAPI
 * validation body. `ApiResponsePayloadError` extends `ApiError`, so it is checked
 * first.
 *
 * Only status 400 keeps the server's text, because that is the one status where the
 * backend sends its own vetted wording — a rejected URL, or a link too deep to
 * identify. Every other unmapped status, a 422 validation body included, falls back
 * instead of echoing the server.
 */
export function getSharePointSiteErrorMessage(error: unknown): string {
  if (error instanceof ApiResponsePayloadError) return kFallbackMessage
  if (error instanceof ApiError) {
    if (error.status >= 500) return 'SharePoint could not be reached right now. Try again shortly.'
    const mapped = kMessageByStatus[error.status]
    if (mapped) return mapped
    if (error.status === 400 && error.message) return error.message
    return kFallbackMessage
  }
  return kFallbackMessage
}

/** Map search failures to safe, actionable messages for this endpoint. */
export function getSharePointSiteSearchErrorMessage(error: unknown): string {
  if (error instanceof ApiResponsePayloadError) return kSearchFallbackMessage
  if (error instanceof ApiError) {
    if (error.status === 400) return 'SharePoint could not search for that term. Try a different name.'
    if ([401, 403, 409].includes(error.status)) return kReconnectMessage
    if (error.status === 429) return kRateLimitMessage
  }
  return kSearchFallbackMessage
}
